import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ChatBubbleOvalLeftIcon,
  MagnifyingGlassIcon,
  ClockIcon,
  EyeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  HashtagIcon,
  HeartIcon,
  BookmarkIcon,
  ShareIcon
} from '@heroicons/react/24/outline';
import { FireIcon as FireSolidIcon } from '@heroicons/react/24/solid';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const ForumDetail = () => {
  const { forumId } = useParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [filterBy, setFilterBy] = useState('all');
  const [loading, setLoading] = useState(true);
  const [forum, setForum] = useState(null);
  const [threads, setThreads] = useState([]);
  const [pinnedThreads, setPinnedThreads] = useState([]);

  useEffect(() => {
    // Sample forum data based on forumId
    const forumData = {
      1: {
        id: 1,
        name: "Medical Research & Innovation",
        description: "Latest developments in medical research, new treatments, and healthcare innovations",
        category: "medical",
        totalThreads: 124,
        totalPosts: 1840,
        totalMembers: 342,
        image: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=800&h=400&fit=crop",
        color: "from-blue-500 to-indigo-600",
        moderators: [
          { name: "Dr. Sarah Johnson", avatar: "https://images.unsplash.com/photo-1494790108755-2616b9b3b191?w=50&h=50&fit=crop&crop=face" },
          { name: "Dr. Michael Chen", avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=50&h=50&fit=crop&crop=face" }
        ],
        rules: [
          "Share evidence-based research and findings",
          "Cite sources for medical claims",
          "Respect professional opinions and debates",
          "No promotion of unverified treatments"
        ]
      },
      2: {
        id: 2,
        name: "Staff Wellness & Mental Health",
        description: "Support programs, wellness initiatives, and mental health resources for healthcare professionals",
        category: "wellness",
        totalThreads: 89,
        totalPosts: 1245,
        totalMembers: 278,
        image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=800&h=400&fit=crop",
        color: "from-emerald-500 to-teal-600",
        moderators: [
          { name: "Dr. Lisa Rodriguez", avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=50&h=50&fit=crop&crop=face" },
          { name: "Sarah Wilson", avatar: "https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?w=50&h=50&fit=crop&crop=face" }
        ],
        rules: [
          "Maintain confidentiality and privacy",
          "Be supportive and respectful",
          "Share resources and experiences constructively",
          "Seek professional help when needed"
        ]
      }
    };

    // Sample threads data
    const threadsData = [
      {
        id: 1,
        title: "New AI-Powered Diagnostic Tools: Revolutionary Impact on Medical Imaging",
        content: "Recent advances in artificial intelligence are transforming how we approach medical diagnostics, particularly in radiology and pathology...",
        author: {
          name: "Dr. Rajesh Kumar",
          avatar: "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=50&h=50&fit=crop&crop=face",
          department: "Radiology",
          title: "Senior Radiologist",
          joinDate: "March 2020"
        },
        createdAt: "2024-07-23T08:30:00Z",
        lastActivity: "2024-07-23T14:45:00Z",
        lastReply: {
          author: "Dr. Meera Sinha",
          timestamp: "2024-07-23T14:45:00Z"
        },
        replies: 18,
        views: 234,
        likes: 42,
        isPinned: true,
        isHot: true,
        isLocked: false,
        tags: ["AI", "Diagnostics", "Radiology", "Innovation"],
        category: "Research"
      },
      {
        id: 2,
        title: "Implementation of Telemedicine: Best Practices and Lessons Learned",
        content: "As we continue to expand our telemedicine capabilities, I wanted to share some insights from our recent implementation...",
        author: {
          name: "Dr. Priya Sharma",
          avatar: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=50&h=50&fit=crop&crop=face",
          department: "Internal Medicine",
          title: "Chief of Medicine",
          joinDate: "January 2019"
        },
        createdAt: "2024-07-22T16:20:00Z",
        lastActivity: "2024-07-23T11:30:00Z",
        lastReply: {
          author: "Dr. Amit Singh",
          timestamp: "2024-07-23T11:30:00Z"
        },
        replies: 24,
        views: 189,
        likes: 38,
        isPinned: false,
        isHot: true,
        isLocked: false,
        tags: ["Telemedicine", "Digital Health", "Patient Care"],
        category: "Discussion"
      },
      {
        id: 3,
        title: "Research Collaboration Opportunities: Multi-center Clinical Trials",
        content: "I'm reaching out to explore potential collaboration opportunities for upcoming multi-center clinical trials...",
        author: {
          name: "Dr. Sushma Iyer",
          avatar: "https://images.unsplash.com/photo-1594824388853-5a7a6b1ab4b9?w=50&h=50&fit=crop&crop=face",
          department: "Cardiac Surgery",
          title: "Lead Surgeon",
          joinDate: "August 2018"
        },
        createdAt: "2024-07-21T09:15:00Z",
        lastActivity: "2024-07-23T09:20:00Z",
        lastReply: {
          author: "Dr. Vikram Malhotra",
          timestamp: "2024-07-23T09:20:00Z"
        },
        replies: 31,
        views: 456,
        likes: 67,
        isPinned: false,
        isHot: false,
        isLocked: false,
        tags: ["Clinical Trials", "Research", "Collaboration"],
        category: "Research"
      },
      {
        id: 4,
        title: "Innovation in Minimally Invasive Surgical Techniques",
        content: "Recent developments in laparoscopic and robotic surgery have opened new possibilities for patient care...",
        author: {
          name: "Dr. James Wilson",
          avatar: "https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=50&h=50&fit=crop&crop=face",
          department: "General Surgery",
          title: "Senior Surgeon",
          joinDate: "June 2017"
        },
        createdAt: "2024-07-20T14:30:00Z",
        lastActivity: "2024-07-22T16:45:00Z",
        lastReply: {
          author: "Dr. Lisa Park",
          timestamp: "2024-07-22T16:45:00Z"
        },
        replies: 15,
        views: 298,
        likes: 29,
        isPinned: false,
        isHot: false,
        isLocked: false,
        tags: ["Surgery", "Minimally Invasive", "Technology"],
        category: "Innovation"
      },
      {
        id: 5,
        title: "Interdisciplinary Team Approach to Complex Cases",
        content: "Discussing the importance of collaborative care in complex medical cases and sharing successful examples...",
        author: {
          name: "Dr. Anna Rodriguez",
          avatar: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=50&h=50&fit=crop&crop=face",
          department: "Emergency Medicine",
          title: "ER Director",
          joinDate: "February 2020"
        },
        createdAt: "2024-07-19T11:45:00Z",
        lastActivity: "2024-07-21T13:20:00Z",
        lastReply: {
          author: "Dr. Robert Kim",
          timestamp: "2024-07-21T13:20:00Z"
        },
        replies: 22,
        views: 178,
        likes: 35,
        isPinned: false,
        isHot: false,
        isLocked: false,
        tags: ["Teamwork", "Complex Cases", "Collaboration"],
        category: "Discussion"
      }
    ];

    // Simulate loading
    setTimeout(() => {
      const currentForum = forumData[forumId] || forumData[1];
      setForum(currentForum);
      
      // Separate pinned and regular threads
      const pinned = threadsData.filter(thread => thread.isPinned);
      const regular = threadsData.filter(thread => !thread.isPinned);
      
      setPinnedThreads(pinned);
      setThreads(regular);
      setLoading(false);
    }, 1000);
  }, [forumId]);

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInHours = Math.floor((now - time) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return time.toLocaleDateString();
  };

  const handleThreadClick = (threadId) => {
    navigate(`/forums/thread/${threadId}`);
  };

  const handleLikeThread = (threadId) => {
    setThreads(prev => 
      prev.map(thread => 
        thread.id === threadId 
          ? { ...thread, likes: thread.likes + 1 }
          : thread
      )
    );
  };

  const filteredThreads = threads.filter(thread => {
    const matchesSearch = thread.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         thread.content.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterBy === 'all' || 
                         (filterBy === 'hot' && thread.isHot) ||
                         (filterBy === 'recent' && new Date(thread.lastActivity) > new Date(Date.now() - 24*60*60*1000));
    return matchesSearch && matchesFilter;
  });

  const sortedThreads = [...filteredThreads].sort((a, b) => {
    switch (sortBy) {
      case 'recent':
        return new Date(b.lastActivity) - new Date(a.lastActivity);
      case 'popular':
        return b.likes - a.likes;
      case 'replies':
        return b.replies - a.replies;
      case 'views':
        return b.views - a.views;
      default:
        return 0;
    }
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-apollo-gray">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-apollo-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Loading forum discussions...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      
      {/* Forum Header */}
      <div className={`bg-gradient-to-r ${forum.color} text-white relative overflow-hidden`}>
        <div className="absolute inset-0 opacity-10">
          <img src={forum.image} alt={forum.name} className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
          {/* Breadcrumb */}
          <div className="flex items-center space-x-2 text-white/70 mb-4 sm:mb-6">
            <button 
              onClick={() => navigate('/forums')}
              className="hover:text-white transition-colors flex items-center space-x-1 text-sm sm:text-base"
            >
              <ChevronLeftIcon className="w-4 h-4" />
              <span>Forums</span>
            </button>
            <ChevronRightIcon className="w-4 h-4" />
            <span className="text-white truncate text-sm sm:text-base">{forum.name}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
            <div className="lg:col-span-2">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">{forum.name}</h1>
              <p className="text-base sm:text-lg lg:text-xl opacity-90 mb-4 sm:mb-6">{forum.description}</p>
              
              {/* Forum Stats */}
              <div className="flex items-center space-x-4 sm:space-x-6 lg:space-x-8">
                <div className="text-center">
                  <div className="text-lg sm:text-xl lg:text-2xl font-bold">{forum.totalThreads}</div>
                  <div className="text-xs sm:text-sm opacity-75">Discussions</div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-xl lg:text-2xl font-bold">{forum.totalPosts.toLocaleString()}</div>
                  <div className="text-xs sm:text-sm opacity-75">Posts</div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-xl lg:text-2xl font-bold">{forum.totalMembers}</div>
                  <div className="text-xs sm:text-sm opacity-75">Members</div>
                </div>
              </div>
            </div>

            {/* Forum Info Card */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 sm:p-6 mt-6 lg:mt-0">
              <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Forum Moderators</h3>
              <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                {forum.moderators.map((moderator, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <img
                      src={moderator.avatar}
                      alt={moderator.name}
                      className="w-6 h-6 sm:w-8 sm:h-8 rounded-full"
                    />
                    <span className="text-xs sm:text-sm truncate">{moderator.name}</span>
                  </div>
                ))}
              </div>
              
              <h4 className="text-xs sm:text-sm font-semibold mb-2">Forum Guidelines</h4>
              <ul className="text-xs sm:text-sm opacity-90 space-y-1">
                {forum.rules.map((rule, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <span className="text-apollo-accent">•</span>
                    <span>{rule}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Thread Controls */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-center justify-between">
            {/* Search and New Thread */}
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              <div className="relative flex-1 max-w-lg">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search discussions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-8 sm:pl-10 pr-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-apollo-accent focus:border-transparent outline-none transition-all duration-200 text-sm sm:text-base"
                />
              </div>
              <button className="bg-apollo-accent text-white px-4 py-2 sm:py-3 rounded-xl hover:bg-apollo-accent/90 transition-colors font-medium flex items-center justify-center space-x-2 text-sm sm:text-base">
                <PlusIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>New Discussion</span>
              </button>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <select
                value={filterBy}
                onChange={(e) => setFilterBy(e.target.value)}
                className="px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-apollo-accent focus:border-transparent outline-none transition-all duration-200 bg-white text-sm sm:text-base"
              >
                <option value="all">All Threads</option>
                <option value="hot">Hot Topics</option>
                <option value="pinned">Pinned</option>
                <option value="answered">Answered</option>
                <option value="unanswered">Unanswered</option>
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-apollo-accent focus:border-transparent outline-none transition-all duration-200 bg-white text-sm sm:text-base"
              >
                <option value="recent">Most Recent</option>
                <option value="popular">Most Popular</option>
                <option value="replies">Most Replies</option>
                <option value="views">Most Views</option>
              </select>
            </div>
          </div>
        </div>

        {/* Pinned Threads */}
        {pinnedThreads.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <HashtagIcon className="w-5 h-5 mr-2 text-apollo-accent" />
              Pinned Discussions
            </h2>
            <div className="space-y-4">
              {pinnedThreads.map(thread => (
                <div
                  key={thread.id}
                  className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6 border border-apollo-accent/20 cursor-pointer"
                  onClick={() => handleThreadClick(thread.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-3">
                        <span className="bg-apollo-accent text-white text-xs px-2 py-1 rounded-full">📌 Pinned</span>
                        {thread.isHot && (
                          <span className="bg-red-100 text-red-600 text-xs px-2 py-1 rounded-full flex items-center">
                            <FireSolidIcon className="w-3 h-3 mr-1" />
                            Hot
                          </span>
                        )}
                        <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">
                          {thread.category}
                        </span>
                      </div>
                      
                      <h3 className="text-xl font-bold text-apollo-primary mb-2 hover:text-apollo-accent transition-colors">
                        {thread.title}
                      </h3>
                      
                      <p className="text-gray-600 mb-4 line-clamp-2">
                        {thread.content}
                      </p>

                      <div className="flex items-center space-x-6 text-sm text-gray-500">
                        <div className="flex items-center space-x-2">
                          <img
                            src={thread.author.avatar}
                            alt={thread.author.name}
                            className="w-6 h-6 rounded-full"
                          />
                          <span>{thread.author.name}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <ClockIcon className="w-4 h-4" />
                          <span>{formatTimeAgo(thread.lastActivity)}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <ChatBubbleOvalLeftIcon className="w-4 h-4" />
                          <span>{thread.replies} replies</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <EyeIcon className="w-4 h-4" />
                          <span>{thread.views} views</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <HeartIcon className="w-4 h-4" />
                          <span>{thread.likes} likes</span>
                        </div>
                      </div>
                    </div>
                    <ChevronRightIcon className="w-5 h-5 text-gray-400 flex-shrink-0 ml-4" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Regular Threads */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            All Discussions ({sortedThreads.length})
          </h2>
          <div className="space-y-4">
            {sortedThreads.map(thread => (
              <div
                key={thread.id}
                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6 border border-gray-100 hover:border-apollo-accent/30 cursor-pointer"
                onClick={() => handleThreadClick(thread.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-3">
                      {thread.isHot && (
                        <span className="bg-red-100 text-red-600 text-xs px-2 py-1 rounded-full flex items-center">
                          <FireSolidIcon className="w-3 h-3 mr-1" />
                          Hot
                        </span>
                      )}
                      <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">
                        {thread.category}
                      </span>
                      {thread.tags.map(tag => (
                        <span key={tag} className="bg-apollo-accent/10 text-apollo-accent text-xs px-2 py-1 rounded-full">
                          #{tag}
                        </span>
                      ))}
                    </div>
                    
                    <h3 className="text-xl font-bold text-apollo-primary mb-2 hover:text-apollo-accent transition-colors">
                      {thread.title}
                    </h3>
                    
                    <p className="text-gray-600 mb-4 line-clamp-2">
                      {thread.content}
                    </p>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-6 text-sm text-gray-500">
                        <div className="flex items-center space-x-2">
                          <img
                            src={thread.author.avatar}
                            alt={thread.author.name}
                            className="w-6 h-6 rounded-full"
                          />
                          <div>
                            <div className="font-medium text-gray-900">{thread.author.name}</div>
                            <div className="text-xs">{thread.author.department}</div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1">
                          <ClockIcon className="w-4 h-4" />
                          <span>{formatTimeAgo(thread.lastActivity)}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <ChatBubbleOvalLeftIcon className="w-4 h-4" />
                          <span>{thread.replies}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <EyeIcon className="w-4 h-4" />
                          <span>{thread.views}</span>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLikeThread(thread.id);
                          }}
                          className="flex items-center space-x-1 text-gray-500 hover:text-red-500 transition-colors"
                        >
                          <HeartIcon className="w-4 h-4" />
                          <span className="text-sm">{thread.likes}</span>
                        </button>
                        <button className="text-gray-400 hover:text-apollo-accent transition-colors">
                          <BookmarkIcon className="w-4 h-4" />
                        </button>
                        <button className="text-gray-400 hover:text-apollo-accent transition-colors">
                          <ShareIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <ChevronRightIcon className="w-5 h-5 text-gray-400 flex-shrink-0 ml-4" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-center space-x-2">
          <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            Previous
          </button>
          <div className="flex items-center space-x-1">
            <button className="px-3 py-2 bg-apollo-accent text-white rounded-lg">1</button>
            <button className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">2</button>
            <button className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">3</button>
            <span className="px-2 text-gray-400">...</span>
            <button className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">8</button>
          </div>
          <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            Next
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ForumDetail;
